import { useEffect, useRef, useState } from 'react'
import inspectorApi from '../services/inspector/api'
import type { ExceptionDrawerTuple } from '../services/inspector/api'

interface UseExceptionDrawerDetailResult {
  row: Record<string, any> | null
  exceptionStatus: string | null
  exceptionTagId: number | null
  exceptionType: string | null
  allowIgnore: boolean
  nameShort: string | null
  loading: boolean
  error: string | null
}

const EMPTY: UseExceptionDrawerDetailResult = {
  row: null,
  exceptionStatus: null,
  exceptionTagId: null,
  exceptionType: null,
  allowIgnore: true,
  nameShort: null,
  loading: false,
  error: null,
}

/**
 * Full drawer payload for one exception tuple, keyed by
 * ``fp_code + data_domain + exception_code + object_id``.
 *
 * The single resolver behind both the admin DQ Workbench drawer and (Phase 2)
 * the inspector deep-link mount, so they feed ``ExceptionDrawer`` the same row,
 * status, linked siblings and Why/How blocks. Only fetches while ``enabled``
 * (the drawer is open); a generation ref drops stale responses when the tuple
 * changes mid-flight, and the lifecycle slice is resolved server-side so the
 * caller never has to pass ``status``. ``loading`` drives the drawer's existing
 * skeleton. Modelled on ``useExceptionDetailBlocks``.
 */
export function useExceptionDrawerDetail(
  fpCode: string | undefined,
  tuple: ExceptionDrawerTuple | null | undefined,
  enabled: boolean = true,
): UseExceptionDrawerDetailResult {
  const [state, setState] = useState<UseExceptionDrawerDetailResult>(EMPTY)
  const genRef = useRef(0)

  const dataDomain = tuple?.data_domain
  const exceptionCode = tuple?.exception_code
  const objectId = tuple?.object_id

  useEffect(() => {
    if (!enabled || !fpCode || !dataDomain || !exceptionCode || !objectId) {
      return
    }
    const gen = ++genRef.current
    setState((s) => ({ ...s, loading: true, error: null }))
    inspectorApi
      .getExceptionDrawerDetail(fpCode, {
        data_domain: dataDomain,
        exception_code: exceptionCode,
        object_id: objectId,
      })
      .then((res) => {
        if (gen !== genRef.current) return
        setState({
          row: res.row ?? null,
          exceptionStatus: res.exception_status ?? null,
          exceptionTagId: res.exception_tag_id ?? null,
          exceptionType: res.exception_type ?? null,
          allowIgnore: res.allow_ignore ?? true,
          nameShort: res.name_short ?? null,
          loading: false,
          error: null,
        })
      })
      .catch((e: unknown) => {
        if (gen !== genRef.current) return
        const message =
          e instanceof Error ? e.message : 'Failed to load exception detail'
        setState({ ...EMPTY, error: message })
      })
  }, [fpCode, dataDomain, exceptionCode, objectId, enabled])

  return state
}

export default useExceptionDrawerDetail
